import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { publicApi, mediaUrl } from "../lib/api";
import { getIcon } from "../lib/icons";
import Seo, { SITE_NAME, breadcrumbSchema } from "../components/Seo";

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

export default function LegalCashieringCaseStudy() {
  const { slug } = useParams();
  const [cs, setCs] = useState(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    publicApi.legalCashiering()
      .then((lc) => {
        const list = lc?.case_studies || [];
        const match = slug ? list.find((c) => c.slug === slug) : list[0];
        setCs(match || null);
      })
      .catch(() => setCs(null));
  }, [slug]);

  if (cs === undefined) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-slate-400">Loading…</div>;
  if (!cs) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <Seo title="Case study not found" noindex />
        <h1 className="font-display font-extrabold text-3xl text-navy mb-4">Case study not found</h1>
        <Link to="/legal-cashiering" className="text-teal font-bold inline-flex items-center gap-2"><ArrowLeft size={16} /> Back to Legal Cashiering</Link>
      </div>
    );
  }

  const caseUrl = `${origin()}/legal-cashiering/case-study/${cs.slug}`;
  return (
    <div className="bg-white" data-testid="lc-case-study-page">
      <Seo title={`${cs.title} — Legal Cashiering Case Study`} description={cs.summary_quote || cs.title}
        jsonLd={[breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Legal Cashiering", path: "/legal-cashiering" },
          { name: cs.client, path: `/legal-cashiering/case-study/${cs.slug}` },
        ])]} />

      {/* Hero */}
      <section className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <Link to="/legal-cashiering" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm font-semibold mb-8" data-testid="lc-cs-back"><ArrowLeft size={15} /> Legal Cashiering</Link>
          <span className="block text-teal font-bold text-sm tracking-widest uppercase mb-3">Client Success Story</span>
          <h1 className="font-display font-extrabold text-3xl md:text-5xl leading-tight max-w-3xl">{cs.title}</h1>
          {cs.logo && (
            <div className="mt-8 inline-flex bg-white rounded-xl px-5 py-3">
              <img loading="lazy" decoding="async" src={mediaUrl(cs.logo)} alt={cs.client} className="h-9 md:h-10 w-auto object-contain" />
            </div>
          )}
        </div>
      </section>

      {/* Overview panel */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 -mt-8 md:-mt-10 relative z-10">
        <div className="bg-white rounded-2xl border border-navy/5 shadow-lg p-6 md:p-8 grid sm:grid-cols-3 gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-teal mb-1">Client</p>
            <p className="font-display font-bold text-navy">{cs.client}</p>
            {cs.sector && <p className="text-sm text-slate-500 mt-0.5">{cs.sector}</p>}
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-teal mb-1">Software</p>
            <ul className="text-sm text-navy font-semibold space-y-0.5">{(cs.software || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-teal mb-1">Services provided</p>
            <ul className="text-sm text-slate-600 space-y-0.5">{(cs.services || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
        </div>
      </section>

      {/* Stats */}
      {cs.stats?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 md:px-12 pt-14">
          <div className="grid sm:grid-cols-3 gap-5">
            {cs.stats.map((s, i) => (
              <div key={i} className="text-center bg-teal/5 border border-teal/15 rounded-2xl p-6" data-testid={`lc-cs-stat-${i}`}>
                <p className="font-display font-extrabold text-3xl md:text-4xl text-teal">{s.value}</p>
                <p className="text-sm text-slate-500 mt-2 leading-snug">{s.label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Challenge */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-14">
        <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-5">The Challenge</h2>
        <div className="space-y-4 text-slate-600 leading-relaxed">{(cs.challenge || []).map((p, i) => <p key={i}>{p}</p>)}</div>
      </section>

      {/* Solution */}
      <section className="bg-slate-50 py-14">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-5">The CASS Solution</h2>
          {cs.solution_intro && <p className="text-slate-600 leading-relaxed mb-6">{cs.solution_intro}</p>}
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-6">
            {(cs.solution_points || []).map((p, i) => (
              <div key={i} className="flex items-start gap-2.5 text-navy font-medium">
                <CheckCircle2 size={18} className="text-teal shrink-0 mt-0.5" /> <span>{p}</span>
              </div>
            ))}
          </div>
          {cs.solution_outro && <p className="text-slate-600 leading-relaxed">{cs.solution_outro}</p>}
        </div>
      </section>

      {/* Results */}
      {cs.results?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 md:px-12 py-16">
          <div className="max-w-2xl mb-10">
            <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-3">The Results</h2>
            {cs.results_intro && <p className="text-slate-500 leading-relaxed">{cs.results_intro}</p>}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {cs.results.map((r, i) => {
              const Icon = getIcon(r.icon);
              return (
                <div key={i} className="bg-white rounded-2xl border border-navy/5 shadow-sm p-6" data-testid={`lc-cs-result-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-teal/10 flex items-center justify-center mb-4">
                    {Icon && <Icon size={20} className="text-teal" />}
                  </div>
                  <h3 className="font-display font-bold text-navy mb-2">{r.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{r.text}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Why CASS */}
      {cs.why?.length > 0 && (
        <section className="bg-navy text-white py-16">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-5">{cs.why_heading || "Why CASS?"}</h2>
            <div className="space-y-4 text-slate-300 leading-relaxed">{cs.why.map((p, i) => <p key={i}>{p}</p>)}</div>
            {cs.benefits?.length > 0 && (
              <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {cs.benefits.map((b, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-white/90"><CheckCircle2 size={17} className="text-teal shrink-0 mt-0.5" /> <span>{b}</span></div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Closing quote */}
      {cs.closing_quote && (
        <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 text-center">
          <p className="font-display font-semibold text-xl md:text-2xl text-navy leading-relaxed">“{cs.closing_quote}”</p>
        </section>
      )}

      {/* CTA */}
      <section className="bg-teal text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mb-4">Looking for a legal cashiering partner?</h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">Whether you need fully outsourced legal cashiering or extra support for your existing finance team, we'd love to show you how CASS can help.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" data-testid="lc-cs-cta-primary" className="inline-flex items-center gap-2 bg-white text-teal-dark font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg">Book a Discovery Call <ArrowRight size={18} /></Link>
            <a href="tel:+441827780550" data-testid="lc-cs-cta-secondary" className="inline-flex items-center gap-2 border-2 border-white/40 text-white font-bold px-8 py-4 rounded-full transition-all hover:bg-white/10"><Phone size={16} /> Speak to Our Team</a>
          </div>
        </div>
      </section>
    </div>
  );
}
